import { MouseEvent, useState, useSyncExternalStore } from 'react'
import { ActionIcon, Button, Group, Menu, Modal, Stack, Text, TextInput, Tooltip } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconChevronDown, IconX } from '@tabler/icons-react'
import { useDeskDecision } from 'hooks/desk/useDeskDecision'
import { useAppTranslation } from 'hooks/useTranslation'
import { DeskRejectReason } from 'supabase/desk/rejectCandidate'
import { isAlreadyDecided, mapDeskError } from './deskErrors'
import { DESK_TAB_LABEL_KEYS, DESK_TABS } from './deskTabs'

type Translate = (key: string, options?: Record<string, unknown>) => string

/** The five reasons, in the UI-SPEC §C order. The values are 0035's closed set. */
export const REJECT_REASONS: DeskRejectReason[] = ['not_an_event', 'wrong_area', 'duplicate', 'past', 'other']

const REJECT_NOTE_MAX = 200

const stop = (event: MouseEvent) => event.stopPropagation()

/** The translated name of a desk tab the RPC returned; an unknown value reads as is. */
export const deskTabName = (t: Translate, tab: string): string =>
  (DESK_TABS as readonly string[]).includes(tab)
    ? t(DESK_TAB_LABEL_KEYS[tab as keyof typeof DESK_TAB_LABEL_KEYS])
    : tab

/** A failed desk write as a red notification (D-11): never a raw code, never a success. */
export const showDeskError = (t: Translate, error: unknown) => {
  const mapped = mapDeskError(error)
  notifications.show({
    color: 'red',
    title: mapped.titleKey ? t(mapped.titleKey) : undefined,
    message: t(mapped.messageKey)
  })
}

// ---------------------------------------------------------------------------
// The note modal. It must outlive the row (the row leaves the list the moment
// it is rejected, and the candidate screen moves on to the next candidate), so
// it lives in a tiny store and is drawn by <RejectNoteHost />, mounted once per
// page, the way Mantine's own notifications are.
// ---------------------------------------------------------------------------

let noteTarget: number | null = null
const noteListeners = new Set<() => void>()

const setNoteTarget = (id: number | null) => {
  noteTarget = id
  noteListeners.forEach((listener) => listener())
}

const subscribeNote = (listener: () => void) => {
  noteListeners.add(listener)
  return () => {
    noteListeners.delete(listener)
  }
}

const readNoteTarget = () => noteTarget

/** Open "Note on this rejection" for a rejected candidate. */
export const openRejectNote = (id: number) => setNoteTarget(id)

/** The rejection was undone: its note modal, if open, has nothing to note (AD-IN-07). */
export const closeRejectNoteFor = (id: number) => {
  if (noteTarget === id) setNoteTarget(null)
}

/**
 * "Note on this rejection" (UI-SPEC §C). Save note writes desk_reject_note
 * only; Keep without a note writes nothing. The rejection stands either way.
 */
export const RejectNoteHost = () => {
  const { t } = useAppTranslation()
  const { saveNote } = useDeskDecision()
  const id = useSyncExternalStore(subscribeNote, readNoteTarget)
  const [note, setNote] = useState('')

  const close = () => {
    setNote('')
    setNoteTarget(null)
  }

  const save = async () => {
    const text = note.trim()
    if (id === null || text === '') return
    try {
      await saveNote.mutateAsync({ id, note: text })
      notifications.show({ color: 'green', message: t('desk.reject.note_saved') })
      close()
    } catch (error) {
      // desk_reject_note writes only on a rejected row: false here means the
      // rejection is gone (undone, restored), so there is nothing to note.
      // Not "decided by someone else" (review AD-IN-07): close quietly.
      if (isAlreadyDecided(error)) {
        close()
        return
      }
      showDeskError(t, error)
    }
  }

  return (
    <Modal opened={id !== null} onClose={close} title={t('desk.reject.note_title')}>
      <Stack gap={16}>
        <TextInput
          label={t('desk.reject.note_label')}
          placeholder={t('desk.reject.note_placeholder')}
          maxLength={REJECT_NOTE_MAX}
          value={note}
          onChange={(event) => setNote(event.currentTarget.value)}
          data-autofocus
        />
        <Group justify="flex-end" gap={8}>
          <Button variant="subtle" color="gray" onClick={close}>
            {t('desk.reject.note_cancel')}
          </Button>
          <Button onClick={save} loading={saveNote.isPending} disabled={note.trim() === ''}>
            {t('desk.reject.note_save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// The toast
// ---------------------------------------------------------------------------

export type ShowRejectedToastParams = {
  t: Translate
  id: number
  /** The candidate title as shown; null reads "Untitled". */
  title: string | null
  reason: DeskRejectReason
  onUndo: () => void
  /** Only for Other: the "Add a note" button. */
  onAddNote?: () => void
}

/**
 * The 8 s "Rejected" notification with Undo (and Add a note for Other). It is
 * the reject's only safety net (D-10: no confirm). Also used by 33-16's
 * "Discard as duplicate".
 */
export const showRejectedToast = ({ t, id, title, reason, onUndo, onAddNote }: ShowRejectedToastParams) => {
  const toastId = `desk-rejected-${id}-${Date.now()}`
  const shownTitle = title?.trim() ? title.trim() : t('desk.untitled')
  notifications.show({
    id: toastId,
    color: 'gray',
    autoClose: 8000, // the Undo window (UI-SPEC §C)
    withCloseButton: true,
    title: t('desk.reject.done_title'),
    message: (
      <Stack gap={8}>
        <Text size="sm">{t('desk.reject.done_body', { title: shownTitle, reason: t(`desk.reject.reasons.${reason}`) })}</Text>
        <Group gap={8}>
          <Button
            size="xs"
            variant="subtle"
            onClick={() => {
              notifications.hide(toastId)
              onUndo()
            }}
          >
            {t('desk.undo')}
          </Button>
          {onAddNote && (
            <Button
              size="xs"
              variant="subtle"
              onClick={() => {
                notifications.hide(toastId)
                onAddNote()
              }}
            >
              {t('desk.reject.add_note')}
            </Button>
          )}
        </Group>
      </Stack>
    )
  })
  return toastId
}

// ---------------------------------------------------------------------------
// The menu
// ---------------------------------------------------------------------------

export type RejectMenuProps = {
  id: number
  title: string | null
  variant: 'icon' | 'button'
  /** Runs before the write (the list removes the row); returns the rollback. */
  onRejecting?: () => (() => void) | void
  /** After a successful reject. */
  onRejected?: () => void
  /** After a successful Undo, with the tab the candidate went back to. */
  onUndone?: (tab: string) => void
}

/**
 * Reject (DESK-05, D-10, UI-SPEC §C): picking a reason IS the reject. One
 * click, no confirm; Undo in the toast for 8 s is the way back.
 */
export const RejectMenu = ({ id, title, variant, onRejecting, onRejected, onUndone }: RejectMenuProps) => {
  const { t } = useAppTranslation()
  const { reject, undo } = useDeskDecision()

  // Runs from the toast, possibly after this row has unmounted: a mutation
  // still executes (and invalidates the desk) when its observer is gone.
  // desk_undo with the stamp the reject returned, never desk_restore: a
  // reject made from the Discard pile goes back to the Discard pile
  // (review AD-WR-11).
  const undoReject = async (decidedAt: string) => {
    try {
      const tab = await undo.mutateAsync({ id, decidedAt })
      closeRejectNoteFor(id)
      notifications.show({ color: 'green', message: t('desk.undone', { tab: deskTabName(t, tab) }) })
      onUndone?.(tab)
    } catch (error) {
      showDeskError(t, error)
    }
  }

  const pick = async (reason: DeskRejectReason) => {
    const rollback = onRejecting?.()
    let decidedAt: string
    try {
      decidedAt = await reject.mutateAsync({ id, reason })
    } catch (error) {
      if (rollback) rollback()
      showDeskError(t, error)
      return
    }
    onRejected?.()
    showRejectedToast({
      t,
      id,
      title,
      reason,
      onUndo: () => void undoReject(decidedAt),
      onAddNote: reason === 'other' ? () => openRejectNote(id) : undefined
    })
  }

  const label = t('desk.reject.button')
  const trigger = variant === 'icon' ? (
    <Tooltip label={label}>
      <ActionIcon variant="subtle" color="red" aria-label={label} disabled={reject.isPending} loading={reject.isPending}>
        <IconX size={16} />
      </ActionIcon>
    </Tooltip>
  ) : (
    <Button
      variant="light"
      color="red"
      rightSection={<IconChevronDown size={16} />}
      disabled={reject.isPending}
      loading={reject.isPending}
    >
      {label}
    </Button>
  )

  return (
    // The dropdown is portalled, but React events still bubble to the row:
    // stop them at the wrapper so a reject never opens the candidate.
    <div onClick={stop} style={{ display: 'inline-block' }}>
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>{trigger}</Menu.Target>
        <Menu.Dropdown onClick={stop}>
          <Menu.Label>{t('desk.reject.menu_label')}</Menu.Label>
          {REJECT_REASONS.map((reason) => (
            <Menu.Item key={reason} onClick={() => pick(reason)}>
              {t(`desk.reject.reasons.${reason}`)}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    </div>
  )
}
